import { useEffect, useState } from 'react';
import { InviteCTA } from './InviteCTA.js';
import { CommunityCTA } from './CommunityCTA.js';
import { trackClient } from '../net/telemetry.js';

/**
 * StallNudge — the quiet "your lobby is stalling" prompt, shared by the phone
 * and desktop lobbies (one component, never two near-copies).
 *
 * The logs' #1 growth leak is a group that assembles UNDER the floor and then
 * just… sits (room EHDL: 4 players, 1h51m in LOBBY, gone). The server already
 * answers 90s of underfull lobby with a voiced NEED_PLAYERS beat through the
 * normal narration path; this is the visual half — after 60 continuous seconds
 * under `minPlayers` it reveals one line in the Ringmaster's voice plus a
 * full-size invite CTA ("Summon your guild").
 *
 * The timer is deliberately keyed on UNDERFULL-ness, not the exact count: a
 * third player trickling into a 5-floor lobby must not reset the clock —
 * only actually reaching the floor does (and losing a seat later re-arms it).
 *
 * Entrance is a soft rise; reduced motion mounts it static (repo rule: keep
 * the timing, drop the movement).
 */

const STALL_MS = 60_000;

function useLobbyStall(underfull: boolean, delayMs = STALL_MS): boolean {
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    if (!underfull) {
      setStalled(false);
      return;
    }
    const t = window.setTimeout(() => setStalled(true), delayMs);
    return () => window.clearTimeout(t);
  }, [underfull, delayMs]);
  return stalled;
}

export function StallNudge({
  code,
  playerCount,
  minPlayers,
  maxWidth = 340,
  style,
  hideLine = false,
}: {
  code: string;
  playerCount: number;
  minPlayers: number;
  maxWidth?: number;
  /**
   * Drop the narrator line above the invite. Set when the surface already shows
   * the AI-seat offer (`ui/TroupeCTA.tsx`), whose "Can't get a group?" says the
   * same thing one control higher up — owner, on PR #45: "right now we have 5
   * lines of text, that is not good". The voiced NEED_PLAYERS beat is
   * unaffected; this is only the caption.
   */
  hideLine?: boolean;
  /** Extra root styles (surface spacing/stacking) — only applied when the
   *  nudge actually renders, so a hidden nudge costs zero layout. */
  style?: React.CSSProperties;
}) {
  const underfull = playerCount < minPlayers;
  const stalled = useLobbyStall(underfull);
  if (!stalled || !underfull) return null;
  const need = minPlayers - playerCount;

  return (
    <div
      className="rm-stall-nudge"
      style={{
        width: '100%',
        maxWidth,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--space-3)',
        textAlign: 'center',
        ...style,
      }}
    >
      {/* The narrator's line — display font, gold ink, same ramp as the other
          Ringmaster asides on these screens. Dropped when the AI-seat offer is
          already on screen saying it in plainer words (see `hideLine`). */}
      {!hideLine && (
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-display)',
            fontSize: 17,
            letterSpacing: '0.04em',
            color: 'var(--gold-5, #e6b866)',
          }}
        >
          An empty house. The show needs {need} more.
        </p>
      )}
      <InviteCTA
        code={code}
        emphasis="primary"
        label="Summon your guild"
        maxWidth={maxWidth}
        onActivated={() => trackClient('stall_nudge_acted')}
      />
      {/* The other half of the answer: 61 of the 79 lobbies that stalled in the
          28 days to 2026-08-27 held exactly ONE player, and "Summon your guild"
          assumes friends already in the channel. Deliberately the quieter of the
          two — inviting people you actually know still converts best, so this
          never outranks the invite (issue #38's falsifier watches for exactly
          that siphon). Shown on the whole underfull path, not solo-only: a table
          of three needing five is also short of players. */}
      <CommunityCTA surface="stall" />
      <style>{`
        .rm-stall-nudge { animation: rm-stall-rise 480ms var(--ease-standard) both; }
        @keyframes rm-stall-rise {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .rm-stall-nudge { animation: none; }
        }
      `}</style>
    </div>
  );
}
